import { html, nothing, type TemplateResult } from 'lit';

export interface TextFieldOptions {
  id: string;
  name: string;
  label: string;
  type?: string;
  autocomplete?: string;
  help?: string;
  error?: string;
  optional?: boolean;
}

/** Bootstrap-styled labelled input with help and error text wired to aria-describedby. */
export function textField(o: TextFieldOptions): TemplateResult {
  const errorId = `${o.id}-error`;
  const helpId = `${o.id}-help`;
  const describedBy = [o.error ? errorId : '', o.help ? helpId : ''].filter(Boolean).join(' ');
  return html`
    <div class="hl-field">
      <label class="form-label" for=${o.id}>${o.label}${o.optional ? html` <span class="text-faint">(optional)</span>` : nothing}</label>
      <input
        id=${o.id}
        name=${o.name}
        type=${o.type ?? 'text'}
        class="form-control ${o.error ? 'is-invalid' : ''}"
        autocomplete=${o.autocomplete ?? 'off'}
        autocapitalize="none"
        spellcheck="false"
        aria-invalid=${o.error ? 'true' : 'false'}
        aria-describedby=${describedBy || nothing}
        data-testid="${o.name}-input"
      />
      ${o.help ? html`<div id=${helpId} class="form-text">${o.help}</div>` : nothing}
      ${o.error ? html`<div id=${errorId} class="hl-field-error">${o.error}</div>` : nothing}
    </div>
  `;
}

/** Reads a form's named fields as strings (checkboxes as booleans via `checkboxes`). */
export function readForm(form: HTMLFormElement, checkboxes: string[] = []): Record<string, string | boolean> {
  const data = new FormData(form);
  const values: Record<string, string | boolean> = {};
  for (const [key, value] of data.entries()) {
    if (typeof value === 'string') values[key] = value;
  }
  for (const name of checkboxes) {
    values[name] = data.has(name);
  }
  return values;
}

/** Moves focus to the first field with an error, so screen readers announce it. */
export function focusFirstError(root: ParentNode, errors: Record<string, string>): void {
  const first = Object.keys(errors).find((field) => errors[field]);
  if (first) root.querySelector<HTMLElement>(`[name="${first}"]`)?.focus();
}
